import React, { useCallback, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography
} from '@mui/material';

const BRAND_TEAL = '#38e8ff';

/**
 * Imperative 2FA prompt for blockchain operations. Blockchain endpoints
 * (token/NFT withdraw + deposit) require a valid TOTP code server-side, so the
 * flow is: `const code = await promptForTwoFactor(); if (!code) return;` then
 * pass `code` to the secure helper. Render `twoFactorModal` in the component.
 */
export function useTwoFactorPrompt() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const resolverRef = useRef<((value: string | null) => void) | null>(null);

  const promptForTwoFactor = useCallback(() => {
    setCode('');
    setOpen(true);
    return new Promise<string | null>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = (value: string | null) => {
    setOpen(false);
    resolverRef.current?.(value);
    resolverRef.current = null;
  };

  const canSubmit = code.length === 6;

  const twoFactorModal = (
    <Dialog
      open={open}
      onClose={() => settle(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 900 }}>Enter your 2FA code</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2, color: 'text.secondary' }}>
          Blockchain transactions require the 6-digit code from your
          authenticator app.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          value={code}
          onChange={(e) =>
            setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
          }
          placeholder="123456"
          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit) settle(code);
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => settle(null)} sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => settle(code)}
          sx={{ backgroundColor: BRAND_TEAL, color: 'white', fontWeight: 800 }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { promptForTwoFactor, twoFactorModal };
}
